import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { paqueteService, PaqueteCreate } from '../../services/paqueteService';
import { sucursalService } from '../../services/sucursalService';
import { clienteService, Cliente } from '../../services/clienteService';
import ClienteForm from '../../components/Clientes/ClienteForm';

const PaqueteEntrada = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showClienteForm, setShowClienteForm] = useState(false);

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data: clientesData } = useQuery(
    'clientes',
    () => clienteService.getAll(1, 200)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<PaqueteCreate & { numeroGuiaEntrada: string; paqueteriaEntrada: string }>({
    defaultValues: {
      tipo: 'externo',
      sucursalId: 0,
      remitenteNombre: '',
      destinatarioNombre: '',
      destinatarioTelefono: '',
      destinatarioDireccion: '',
      numeroGuiaEntrada: '',
      paqueteriaEntrada: ''
    }
  });

  const clienteId = watch('clienteId');

  const createMutation = useMutation(paqueteService.create, {
    onSuccess: (data) => {
      const trackingNumber = data.data.paquete.numeroTracking;
      toast.success(`✓ Paquete registrado correctamente. Tracking: ${trackingNumber}`, {
        autoClose: 5000
      });
      queryClient.invalidateQueries(['paquetes']);
      queryClient.invalidateQueries(['paquetes-recientes']);
      reset();
      // Redirigir después de mostrar el mensaje
      setTimeout(() => {
        navigate('/paquetes/dashboard');
      }, 3000);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al registrar paquete');
    }
  });

  const onSubmit = (data: any) => {
    createMutation.mutate({
      ...data,
      tipo: 'externo',
      numeroGuiaEntrada: data.numeroGuiaEntrada || undefined,
      paqueteriaEntrada: data.paqueteriaEntrada || undefined
    });
  };

  const applyClienteToDestinatario = (cliente: Cliente) => {
    if (cliente.nombre) {
      setValue('destinatarioNombre', cliente.nombre, { shouldValidate: true });
    }
    if (cliente.telefono) {
      setValue('destinatarioTelefono', cliente.telefono, { shouldValidate: true });
    }
    if (cliente.email) {
      setValue('destinatarioEmail', cliente.email);
    }
    if (cliente.direccion) {
      setValue('destinatarioDireccion', cliente.direccion, { shouldValidate: true });
    }
  };

  const handleClienteChange = (clienteId: number) => {
    const cliente = clientesData?.data.clientes.find(c => c.id === clienteId);
    if (cliente) {
      applyClienteToDestinatario(cliente);
    }
  };

  const paqueterias = [
    'FedEx', 'DHL', 'UPS', 'Estafeta', 'Paquetexpress', 
    'Redpack', 'Sendex', 'Correos de México', 'Otro'
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Entrada de Paquete</h1>
          <p className="text-gray-600 mt-1">Registro rápido de paquetes recibidos</p>
        </div>
        <button
          onClick={() => navigate('/paquetes')}
          className="px-4 py-2 text-gray-700 border rounded hover:bg-gray-50"
        >
          ← Volver
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Campos prioritarios */}
          <div className="space-y-4 border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800">Información de Entrada</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Guía de Entrada *
                </label>
                <input
                  {...register('numeroGuiaEntrada', { 
                    required: 'Número de guía es requerido',
                    maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                  })}
                  placeholder="Ej: 1234567890"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.numeroGuiaEntrada && (
                  <p className="text-red-500 text-sm mt-1">{errors.numeroGuiaEntrada.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paquetería de Entrada *
                </label>
                <select
                  {...register('paqueteriaEntrada', { 
                    required: 'Paquetería es requerida',
                    maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                  })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Selecciona paquetería</option>
                  {paqueterias.map((pq) => (
                    <option key={pq} value={pq}>{pq}</option>
                  ))}
                </select>
                {errors.paqueteriaEntrada && (
                  <p className="text-red-500 text-sm mt-1">{errors.paqueteriaEntrada.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sucursal *
                </label>
                <select
                  {...register('sucursalId', { 
                    required: 'Sucursal es requerida',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Selecciona una sucursal' }
                  })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Selecciona sucursal</option>
                  {sucursalesData?.data.sucursales.map((sucursal) => (
                    <option key={sucursal.id} value={sucursal.id}>
                      {sucursal.name}
                    </option>
                  ))}
                </select>
                {errors.sucursalId && (
                  <p className="text-red-500 text-sm mt-1">{errors.sucursalId.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente (Opcional)
                </label>
                <div className="flex space-x-2">
                  <select
                    {...register('clienteId', { valueAsNumber: true })}
                    onChange={(e) => handleClienteChange(Number(e.target.value))}
                    className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={0}>Sin cliente</option>
                    {clientesData?.data.clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre} {cliente.email ? `- ${cliente.email}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowClienteForm(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm whitespace-nowrap"
                  >
                    + Nuevo
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tracking Externo (Opcional)
              </label>
              <input
                {...register('codigoBarras')}
                placeholder="Tracking de la paquetería original"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Destinatario */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Datos del Destinatario</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  {...register('destinatarioNombre', { required: 'Nombre del destinatario es requerido' })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.destinatarioNombre && (
                  <p className="text-red-500 text-sm mt-1">{errors.destinatarioNombre.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  {...register('destinatarioTelefono', { required: 'Teléfono del destinatario es requerido' })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.destinatarioTelefono && (
                  <p className="text-red-500 text-sm mt-1">{errors.destinatarioTelefono.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  {...register('destinatarioEmail')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección *
                </label>
                <input
                  {...register('destinatarioDireccion', { required: 'Dirección del destinatario es requerida' })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.destinatarioDireccion && (
                  <p className="text-red-500 text-sm mt-1">{errors.destinatarioDireccion.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Remitente */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Datos del Remitente</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  {...register('remitenteNombre', { required: 'Nombre del remitente es requerido' })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.remitenteNombre && (
                  <p className="text-red-500 text-sm mt-1">{errors.remitenteNombre.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  {...register('remitenteTelefono')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  {...register('remitenteEmail')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  {...register('remitenteDireccion')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Botón de acción principal */}
          <div className="pt-6 border-t">
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="w-full py-4 bg-primary-600 text-white text-lg font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 transition"
            >
              {createMutation.isLoading ? 'Registrando...' : '✓ Registrar Paquete y Generar Código'}
            </button>
          </div>
        </form>
      </div>

      {showClienteForm && (
        <ClienteForm
          onClose={() => setShowClienteForm(false)}
          onSuccess={(nuevoCliente) => {
            queryClient.invalidateQueries(['clientes']);
            setValue('clienteId', nuevoCliente.id, { shouldValidate: true });
            applyClienteToDestinatario(nuevoCliente);
            setShowClienteForm(false);
          }}
        />
      )}
    </div>
  );
};

export default PaqueteEntrada;

